<template>
  <a-dropdown
    v-model:open="open"
    :overlayStyle="{ width: '360px' }"
    placement="bottomRight"
    :trigger="['click']"
  >
    <button
      v-if="variant === 'sidebar'"
      class="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-transparent cursor-pointer"
      aria-label="Notifications"
    >
      <span class="relative flex-shrink-0">
        <Bell class="w-[18px] h-[18px] stroke-[1.5px]" />
        <span
          v-if="unreadCount > 0"
          class="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </span>
      <span class="text-sm leading-tight flex-1 text-left font-medium">
        {{ label }}
      </span>
    </button>

    <button
      v-else
      class="my-1 relative text-gray-400 hover:text-gray-600 transition-colors p-1.5 cursor-pointer rounded-sm hover:bg-gray-50"
      aria-label="Notifications"
    >
      <Bell class="w-5 h-5" />
      <span
        v-if="unreadCount > 0"
        class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <template #overlay>
      <div
        class="bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-gray-100"
        >
          <span class="text-sm font-semibold text-gray-900">Notifications</span>
          <div class="flex items-center gap-3">
            <button
              v-if="unreadCount > 0"
              class="text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer"
              @click="onMarkAllRead"
            >
              Mark all read
            </button>
            <button
              v-if="items.length"
              class="text-xs font-medium text-gray-500 hover:text-red-600 cursor-pointer"
              @click="onClearAll"
            >
              Clear all
            </button>
          </div>
        </div>

        <div class="max-h-96 overflow-y-auto">
          <a-spin :spinning="isLoading">
            <template v-if="items.length">
              <div
                v-for="n in items"
                :key="n.uuid"
                role="button"
                tabindex="0"
                class="group w-full text-left px-4 py-3 flex gap-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer"
                :class="!n.is_read ? 'bg-blue-50/40' : ''"
                @click="onItemClick(n)"
                @keydown.enter="onItemClick(n)"
              >
                <span
                  class="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                  :class="!n.is_read ? 'bg-blue-500' : 'bg-transparent'"
                ></span>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ n.title }}
                  </p>
                  <p
                    v-if="n.message"
                    class="text-xs text-gray-500 mt-0.5 line-clamp-2"
                  >
                    {{ n.message }}
                  </p>
                  <p class="text-[11px] text-gray-400 mt-1">
                    {{ formatRelative(n.created) }}
                  </p>
                </div>
                <button
                  class="flex-shrink-0 self-start p-1 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                  aria-label="Delete notification"
                  @click.stop="onDelete(n)"
                >
                  <X class="w-3.5 h-3.5" />
                </button>
              </div>
            </template>
            <div v-else class="px-4 py-10 text-center text-sm text-gray-400">
              No notifications yet
            </div>
          </a-spin>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { Bell, X } from 'lucide-vue-next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useRouter } from 'vue-router';

const router = useRouter();

dayjs.extend(relativeTime);

const props = defineProps({
  mode: {
    type: String,
    default: 'student',
  },
  variant: {
    type: String,
    default: 'default',
  },
  label: {
    type: String,
    default: 'Notifications',
  },
});

const open = ref(false);

const store = useNotificationsStore();
const { items, unreadCount, isLoading } = storeToRefs(store);

function formatRelative(ts) {
  if (!ts) return '';
  return dayjs(ts).fromNow();
}

function resolveTarget(n) {
  const meta = n.metadata || {};
  if (props.mode === 'org') {
    switch (n.type) {
      case 'GRADE_SUBMISSION_RECEIVED':
      case 'GRADE_COMPLETED':
      case 'GRADE_REGRADE_COMPLETED':
        return meta.submission_uuid
          ? {
              name: 'org-grade-for-me-review',
              params: { submissionUuid: meta.submission_uuid },
            }
          : { name: 'org-grade-for-me' };
      // A generated worksheet is the teacher's own draft - the builder
      // dashboard is where it lands.
      case 'WORKSHEET_GENERATED':
        return { name: 'org-worksheet-builder' };
      // Only UPLOAD worksheets are graded through Grade for me; other
      // completed worksheets have nowhere meaningful to link to.
      case 'WORKSHEET_COMPLETED':
        return meta.worksheet_source === 'UPLOAD'
          ? { name: 'org-grade-for-me' }
          : null;
      // An overdue worksheet is about a student's submission, which is
      // reviewed through Grade for me, not the builder.
      case 'WORKSHEET_OVERDUE':
        return { name: 'org-grade-for-me' };
      // Pending join requests surface only in the classroom's Add Student
      // modal, under its Inquiries tab.
      case 'JOIN_REQUEST_CREATED':
        return { name: 'org-classroom', query: { openInquiries: '1' } };
      // Invitations are managed from the teachers/admins tables.
      case 'INVITATION_ACCEPTED':
      case 'INVITATION_DECLINED':
        return { name: 'org-manage-organisation' };
      // Report emails are sent from a student's detail page; go back to the
      // student when we know who it was, else the classroom list.
      case 'PARENT_REPORT_EMAIL_SENT':
      case 'PARENT_REPORT_EMAIL_FAILED':
        return meta.student_uuid
          ? {
              name: 'org-classroom-student-detail',
              params: { studentUuid: meta.student_uuid },
            }
          : { name: 'org-classroom' };
      default:
        return null;
    }
  }

  switch (n.type) {
    case 'INVITATION_RECEIVED':
      return { name: 'my-organisation', query: { openInvitations: '1' } };
    case 'JOIN_REQUEST_APPROVED':
    case 'JOIN_REQUEST_DECLINED':
      return { name: 'my-organisation' };
    case 'WORKSHEET_ASSIGNED':
    case 'WORKSHEET_OVERDUE':
      return { name: 'my-worksheets' };
    default:
      return null;
  }
}

function onItemClick(n) {
  if (!n.is_read) store.markRead(n.uuid);
  const target = resolveTarget(n);
  if (target) {
    open.value = false;
    router.push(target);
  }
}

function onMarkAllRead() {
  store.markAllRead();
}

function onDelete(n) {
  store.deleteNotification(n.uuid);
}

function onClearAll() {
  store.clearAll();
}

watch(open, (isOpen) => {
  if (isOpen) store.fetchNotifications();
});

onMounted(() => {
  store.configure({ mode: props.mode });
  store.startPolling();
});

onBeforeUnmount(() => {
  store.stopPolling();
});
</script>
